import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../utils/api';
import Alert from '../components/Alert';
import { useAuth } from '../context/AuthContext';

export default function Settings() {
  const { isAdmin } = useAuth();
  const [gstNumber, setGstNumber] = useState('');
  const [taxRate, setTaxRate] = useState('');
  const [lowStockQty, setLowStockQty] = useState('50');
  const [lowStockQtyLoose, setLowStockQtyLoose] = useState('5');
  const [lastBackupAt, setLastBackupAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [backingUp, setBackingUp] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    api
      .get('/settings')
      .then((res) => {
        setGstNumber(res.data.settings.gst_number || '');
        setTaxRate(String(res.data.settings.tax_rate ?? '0'));
        setLowStockQty(String(res.data.settings.low_stock_qty ?? '50'));
        setLowStockQtyLoose(String(res.data.settings.low_stock_qty_loose ?? '5'));
        setLastBackupAt(res.data.settings.last_backup_at || null);
      })
      .catch(() => setAlert({ type: 'danger', msg: 'Could not load settings.' }))
      .finally(() => setLoading(false));
  }, []);

  async function handleSave(e) {
    e.preventDefault();
    const rate = parseFloat(taxRate);
    if (isNaN(rate) || rate < 0) {
      setAlert({ type: 'danger', msg: 'Tax rate must be a valid non-negative number.' });
      return;
    }
    const lowStock = parseInt(lowStockQty, 10);
    if (isNaN(lowStock) || lowStock < 0) {
      setAlert({ type: 'danger', msg: 'Low Stock Quantity Alert must be a valid non-negative number.' });
      return;
    }
    const lowStockLoose = parseFloat(lowStockQtyLoose);
    if (isNaN(lowStockLoose) || lowStockLoose < 0) {
      setAlert({ type: 'danger', msg: 'Low Stock Quantity Alert (loose) must be a valid non-negative number.' });
      return;
    }
    setSaving(true);
    setAlert(null);
    try {
      const res = await api.put('/settings', {
        gst_number: gstNumber.trim(),
        tax_rate: rate,
        low_stock_qty: lowStock,
        low_stock_qty_loose: lowStockLoose,
      });
      setGstNumber(res.data.settings.gst_number || '');
      setTaxRate(String(res.data.settings.tax_rate));
      setLowStockQty(String(res.data.settings.low_stock_qty));
      setLowStockQtyLoose(String(res.data.settings.low_stock_qty_loose));
      setAlert({ type: 'success', msg: 'Settings saved successfully.' });
    } catch (err) {
      setAlert({ type: 'danger', msg: err?.response?.data?.error || 'Failed to save settings.' });
    } finally {
      setSaving(false);
    }
  }

  async function handleBackup() {
    setBackingUp(true);
    setAlert(null);
    try {
      const res = await api.get('/backup/download', { responseType: 'blob' });
      const ts = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/gzip' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventory_backup_${ts}.sql.gz`;
      a.click();
      URL.revokeObjectURL(url);
      setLastBackupAt(new Date().toISOString());
      setAlert({ type: 'success', msg: 'Backup downloaded. Keep this file somewhere safe off this server.' });
    } catch {
      setAlert({ type: 'danger', msg: 'Backup failed. Please try again or check server logs.' });
    } finally {
      setBackingUp(false);
    }
  }

  return (
    <Layout title="Settings">
      <Alert type={alert?.type} message={alert?.msg} onClose={() => setAlert(null)} />

      {loading ? (
        <div className="empty-state">Loading settings...</div>
      ) : (
        <div style={{ maxWidth: 480 }}>
          <div className="card">
            <div className="card-header">
              <div className="card-title">Configuration</div>
            </div>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="form-label">GST Number</label>
                <input
                  type="text"
                  placeholder="e.g. 21AABCG1234A1Z5"
                  value={gstNumber}
                  onChange={(e) => setGstNumber(e.target.value)}
                  maxLength={50}
                />
                <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>
                  This number will appear on all newly created invoices.
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Tax Rate (%)</label>
                <input
                  type="number"
                  placeholder="e.g. 1.4"
                  value={taxRate}
                  onChange={(e) => setTaxRate(e.target.value)}
                  min="0"
                  step="0.01"
                  style={{ maxWidth: 160 }}
                />
                <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>
                  Applied as GST on new invoices. Existing invoices keep their original rate.
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Low Stock Quantity Alert</label>
                <input
                  type="number"
                  placeholder="e.g. 50"
                  value={lowStockQty}
                  onChange={(e) => setLowStockQty(e.target.value)}
                  min="0"
                  step="1"
                  style={{ maxWidth: 160 }}
                />
                <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>
                  Packaged products with stock below this number will be flagged as low stock in Inventory.
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Low Stock Quantity Alert (Loose Products) — kg / litre</label>
                <input
                  type="number"
                  placeholder="e.g. 5"
                  value={lowStockQtyLoose}
                  onChange={(e) => setLowStockQtyLoose(e.target.value)}
                  min="0"
                  step="0.001"
                  style={{ maxWidth: 160 }}
                />
                <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>
                  In <strong>kg / litre</strong>. Loose products are flagged as low stock when their
                  weight/volume falls below this — e.g. a value of 5 flags anything under 5 kg (or 5 litre).
                </div>
              </div>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={saving}
                style={{ marginTop: '.25rem' }}
              >
                <i className="ti ti-device-floppy" aria-hidden="true"></i>
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </form>
          </div>

          {isAdmin && (
            <div className="card" style={{ marginTop: '1rem' }}>
              <div className="card-header">
                <div className="card-title">Database Backup</div>
              </div>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleBackup}
                disabled={backingUp}
              >
                <i className="ti ti-database-export" aria-hidden="true"></i>
                {backingUp ? 'Preparing backup…' : 'Download DB Backup'}
              </button>
              <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: '.75rem' }}>
                Last backup taken:{' '}
                <strong>
                  {lastBackupAt ? new Date(lastBackupAt).toLocaleString() : 'Never'}
                </strong>
              </div>
            </div>
          )}
        </div>
      )}
    </Layout>
  );
}
